import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import { AllocationFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { isNumber } from 'modules/core/lib/validateNumberInput'
import app from 'modules/core/store/models/app'

import { useBulkUpdateBudgetAllocation } from 'modules/finance/hooks/useBudgetAllocations'
import {
  allocationsOverBudget,
  sumAllocationsAmountCents,
} from 'modules/finance/lib/budget_allocations'

import { BudgetExceededOptionResultType } from '../../BudgetExceededOptionSelector'
import AllocationPerEmployeeForm from './Form'

interface AllocationPerEmployeeModalProps {
  closeModal: () => void
  currency: string
  budgetLimit: number
  budgetId: number
  allocationIds: number[]
  allocations: AllocationFragment[]
  periodStart: string
  refetchBudget: (param: { id: number; periodStart: string }) => void
}

interface FormValues {
  limitPerEmployee?: string
  newBudgetLimit?: number
}

const AllocationPerEmployeeModal = ({
  closeModal,
  currency,
  budgetLimit,
  budgetId,
  allocationIds,
  allocations,
  periodStart,
  refetchBudget,
}: AllocationPerEmployeeModalProps) => {
  const [displayExtraSection, setDisplayExtraSection] = useState(false)
  const [selectorResult, setSelectorResult] = useState<BudgetExceededOptionResultType>({
    increaseLimit: 0,
    tryAnotherAllocation: 0,
    type: '',
  })
  const { bulkUpdateBudgetAllocation, loading } = useBulkUpdateBudgetAllocation({
    budgetId,
  })

  // for update we need to know the ones not selected only
  const allocationsNotSelected = allocations.filter(
    (allocation) => !allocationIds.includes(allocation.id)
  )

  const validateData = (form: FormikProps<FormValues>) => {
    let valid = true
    const limitPerEmployee = Number(form.values.limitPerEmployee)
    if (!form.values.limitPerEmployee) {
      return
    }

    if (!isNumber(form.values?.limitPerEmployee) || limitPerEmployee < 0) {
      valid = false
      form.setErrors({
        limitPerEmployee: i18n.t('budgets.detail.allocation_per_employee_modal.validate.no_number'),
      })
    }

    if (limitPerEmployee <= 0) {
      valid = false
      form.setErrors({
        limitPerEmployee: i18n.t(
          'budgets.detail.allocation_per_employee_modal.validate.limit_greater'
        ),
      })
    }

    if (
      allocationsOverBudget(
        budgetLimit,
        limitPerEmployee * 100,
        allocationIds.length,
        sumAllocationsAmountCents(allocationsNotSelected)
      ) &&
      selectorResult.type !== 'increaseLimit'
    ) {
      valid = false
      form.setErrors({
        limitPerEmployee: i18n.t(
          'budgets.detail.allocation_per_employee_modal.validate.budget_limit_exceeded'
        ),
      })
      setDisplayExtraSection(true)
    } else {
      setDisplayExtraSection(false)
    }
    return valid
  }

  const formHasErrors = () => {
    return form.errors.limitPerEmployee !== undefined || form.errors.newBudgetLimit !== undefined
  }

  const handleBulkAllocation = async (limit: number, newBudgetLimit?: number) => {
    const response = await bulkUpdateBudgetAllocation({
      ids: allocationIds,
      budgetId: budgetId,
      amountCents: limit,
      budgetLimitAmountCents: newBudgetLimit,
    })

    const result = handleResult(response, (_) => {})

    if (allocationIds.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('budgets.detail.allocation_per_employee_modal.no_selected_employees_error'),
      })
    }

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('budgets.detail.allocation_per_employee_modal.submit_success'),
      })
      refetchBudget({
        id: budgetId,
        periodStart: periodStart,
      })
      closeModal()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('budgets.detail.allocation_per_employee_modal.submit_error'),
      })
    }
    return result
  }

  const form = useForm<FormValues>({
    defaultValues: {},
    onSubmit: async (data) => {
      const isValid = validateData(form)

      if (!isValid) {
        return Promise.reject()
      }

      let limit = Number(data.limitPerEmployee) * 100
      if (selectorResult.type === 'tryAnotherAllocation') {
        limit = selectorResult.tryAnotherAllocation
      }

      if (selectorResult.type === 'increaseLimit') {
        const newBudgetLimit = selectorResult.increaseLimit

        return handleBulkAllocation(limit, newBudgetLimit)
      }

      return handleBulkAllocation(limit)
    },
  })

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('budgets.detail.allocation_per_employee_modal.title')}
      description={i18n.t('budgets.detail.allocation_per_employee_modal.description')}
      onSubmit={() => {
        form.handleSubmit()
      }}
      onClose={closeModal}
      footer={
        <>
          <Button type="secondary" onClick={closeModal}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={loading || formHasErrors()} submit>
            {i18n.t('common.accept')}
          </Button>
        </>
      }
    >
      <AllocationPerEmployeeForm
        currency={currency}
        budgetLimit={budgetLimit}
        totalAllocatedAmountCents={sumAllocationsAmountCents(allocationsNotSelected)}
        form={form}
        displayExtraSection={displayExtraSection}
        setDisplayExtraSection={setDisplayExtraSection}
        allocationIds={allocationIds}
        setSelectorResult={setSelectorResult}
      />
    </Modal>
  )
}

export default AllocationPerEmployeeModal
